import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Iconify from '../../ui-component/iconify';
import TableStyle from '../../ui-component/TableStyle';
import { useState, useEffect } from 'react';
import { Stack, Button, Container, Typography, Box, Card, IconButton, Chip, Avatar, Paper, Badge, Divider } from '@mui/material';
import { toast } from 'react-toastify';
import { getApi } from 'views/services/api';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FoodInvoiceDialog from './FoodInvoiceDialog';

const SeparateFoodView = () => {
  const [openMakeOrder, setOpenMakeOrder] = useState(false);
  const [selectedFoodItems, setSelectedFoodItems] = useState([]);
  const navigate = useNavigate();
  const [itemsData, setItemsData] = useState([]);
  const [quantityMap, setQuantityMap] = useState({});
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';

  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Obtener datos de items del restaurante
  const fetchItemsData = async () => {
    try {
      const response = await getApi(`api/restaurant/viewallitems/${hotel?.hotelId}`);
      console.log('res =>', response);

      const formattedData = response?.data?.restaurantData.map((item) => ({
        ...item,
        itemImage: item.itemImage.split('/').pop()
      }));

      setItemsData(formattedData);

      // Inicializar mapa de cantidades con 0 para cada item
      const initialQuantityMap = {};
      formattedData.forEach((item) => {
        initialQuantityMap[item._id] = 0;
      });
      setQuantityMap(initialQuantityMap);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar los items del menú');
    }
  };

  useEffect(() => {
    fetchItemsData();
  }, [openMakeOrder]);

  const handleMakeOrder = () => {
    setOpenMakeOrder(true);
  };

  const handleCloseMakeOrder = () => {
    setOpenMakeOrder(false);
  };

  const handleAddButtonClick = async () => {
    const selectedItems = itemsData.filter((item) => quantityMap[item._id] > 0);
    const foodItems = selectedItems.map((item) => ({
      id: item._id,
      name: item.itemName,
      price: item.amount,
      quantity: quantityMap[item._id]
    }));

    try {
      if (foodItems.length === 0) {
        toast.error('Por favor selecciona al menos un item');
      } else {
        setSelectedFoodItems(foodItems);
        handleMakeOrder();
      }
    } catch (error) {
      console.log(error);
      toast.error('No se pueden agregar los items de comida');
    }
  };

  const handleIncrementQuantity = (itemId) => {
    setQuantityMap((prevQuantityMap) => ({
      ...prevQuantityMap,
      [itemId]: (prevQuantityMap[itemId] || 0) + 1
    }));
  };

  const handleDecrementQuantity = (itemId) => {
    setQuantityMap((prevQuantityMap) => ({
      ...prevQuantityMap,
      [itemId]: Math.max((prevQuantityMap[itemId] || 0) - 1, 0)
    }));
  };

  // Calcular total de items seleccionados
  const getTotalItems = () => {
    return Object.values(quantityMap).reduce((sum, quantity) => sum + quantity, 0);
  };

  // Calcular total del precio
  const getTotalPrice = () => {
    return itemsData.reduce((sum, item) => {
      const quantity = quantityMap[item._id] || 0;
      return sum + item.amount * quantity;
    }, 0);
  };

  const columns = [
    {
      field: 'itemImage',
      headerName: 'Imagen',
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Avatar
            src={`${baseUrl}api/uploads/restaurant/Item/${params.value}`}
            alt={params.row.itemName}
            variant="rounded"
            sx={{
              width: 50,
              height: 50,
              border: '2px solid',
              borderColor: 'divider',
              boxShadow: 1
            }}
          />
        </Box>
      )
    },
    {
      field: 'itemName',
      headerName: 'Nombre del Item',
      flex: 1.5,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
          {params.row.category && <Chip label={params.row.category} size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />}
        </Box>
      )
    },
    {
      field: 'amount',
      headerName: 'Precio Unitario',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {currency} {params.value.toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'quantity',
      headerName: 'Cantidad',
      flex: 1.2,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            size="small"
            onClick={() => handleDecrementQuantity(params.row._id)}
            disabled={!quantityMap[params.row._id]}
            sx={{
              bgcolor: 'error.lighter',
              color: 'error.main',
              '&:hover': { bgcolor: 'error.light' },
              '&:disabled': { bgcolor: 'action.disabledBackground' }
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>

          <Chip
            label={quantityMap[params.row._id] || 0}
            size="small"
            sx={{
              minWidth: 40,
              fontWeight: 700,
              bgcolor: quantityMap[params.row._id] > 0 ? 'primary.lighter' : 'grey.100',
              color: quantityMap[params.row._id] > 0 ? 'primary.main' : 'text.secondary'
            }}
          />

          <IconButton
            size="small"
            onClick={() => handleIncrementQuantity(params.row._id)}
            sx={{
              bgcolor: 'success.lighter',
              color: 'success.main',
              '&:hover': { bgcolor: 'success.light' }
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
      )
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 1,
      renderCell: (params) => {
        const itemPrice = params.row.amount;
        const itemQuantity = quantityMap[params.row._id] || 0;
        const totalPrice = itemPrice * itemQuantity;
        return (
          <Typography variant="body1" fontWeight={700} color={totalPrice > 0 ? 'primary.main' : 'text.secondary'}>
            {currency} {totalPrice.toFixed(2)}
          </Typography>
        );
      }
    }
  ];

  return (
    <>
      <FoodInvoiceDialog open={openMakeOrder} handleClose={handleCloseMakeOrder} data={selectedFoodItems} />
      <Container maxWidth="xl">
        {/* Header mejorado */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'primary.main',
                  boxShadow: 2
                }}
              >
                <RestaurantIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  Crear Orden de Comida
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecciona los items para crear una orden separada
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.secondary',
                  bgcolor: 'action.hover'
                }
              }}
            >
              Regresar
            </Button>
          </Stack>

          {/* Resumen de selección */}
          {getTotalItems() > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'success.lighter',
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 2
              }}
            >
              <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Badge badgeContent={getTotalItems()} color="success" max={99}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <ShoppingCartIcon />
                    </Avatar>
                  </Badge>
                  <Box>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      {getTotalItems()} {getTotalItems() === 1 ? 'Item Seleccionado' : 'Items Seleccionados'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de la Orden: {currency} {getTotalPrice().toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  variant="contained"
                  size="large"
                  color="success"
                  startIcon={<ReceiptIcon />}
                  onClick={handleAddButtonClick}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 5
                    }
                  }}
                >
                  Realizar Orden
                </Button>
              </Stack>
            </Paper>
          )}
        </Box>

        {/* Tabla mejorada */}
        <TableStyle>
          <Box width="100%">
            <Card
              sx={{
                height: '600px',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={600}>
                    Menú del Restaurante
                  </Typography>
                  <Chip icon={<RestaurantIcon />} label={`${itemsData?.length} Items Disponibles`} color="primary" variant="outlined" />
                </Stack>
              </Box>

              <DataGrid
                rows={itemsData}
                columns={columns}
                getRowId={(row) => row._id}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 }
                  }
                }}
                rowHeight={70}
                disableRowSelectionOnClick
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: 'grey.100',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    fontSize: '0.9rem',
                    fontWeight: 700
                  },
                  '& .MuiDataGrid-row:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              />
            </Card>
          </Box>
        </TableStyle>
      </Container>
    </>
  );
};

export default SeparateFoodView;
